import React, { Component, useContext } from 'react';
import { Row, Container, Col, Form, Button, Modal, Spinner, Alert } from "react-bootstrap";
import { Pagination, TableHeader, TableBody, Filter, useDatatableLifecycle, shouldTableUpdate } from 'react-bs-datatable';
import { fetchProtocolsByPrimarySite, toSiteIdsParam } from '../lib/PrimarySiteUtils';

// The local datatable wrapper. The same block is copy-pasted into ten other
// screens (ProtocolMaster, MedicineMaster, Dashboard, ...) — this is the
// house convention, not a new one.
//
// Differs from those copies in one way, both deliberate:
//  - the table is not text-nowrap. Primary Sites can hold four site names
//    joined with ", ", which would otherwise push the table wider than the
//    modal and force horizontal scrolling; letting cells wrap keeps it inside
//    the page.
const CustomTable = React.memo(props => {
    const { data, tableHeaders, onPageNavigate, classes, onSortChange, tableClass, labels, rowsPerPage, currentPage, sortedProp, maxPage, Components, onChangeFilter, filterText, filterable } = useDatatableLifecycle(props);

    return (
        <div>
            <Components.Row className="controlRow__root">
                <Components.Col xs="12">
                    <Filter
                        classes={classes}
                        tableHeaders={tableHeaders}
                        placeholder='Search by Protocol Name'
                        onChangeFilter={onChangeFilter}
                        filterText={filterText}
                        filterable={filterable}
                    />
                </Components.Col>
            </Components.Row>
            <Components.Row>
                <Components.Col xs="12">
                    <Components.Table className={tableClass}>
                        <TableHeader
                            classes={classes}
                            tableHeaders={tableHeaders}
                            sortedProp={sortedProp}
                            onSortChange={onSortChange}
                            components={{
                                TableHead: Components.TableHead,
                                TableCell: Components.TableCell,
                                TableRow: Components.TableRow
                            }}
                        />
                        <TableBody
                            classes={classes}
                            tableHeaders={tableHeaders}
                            labels={labels}
                            data={data}
                            components={{
                                TableBody: Components.TableBody,
                                TableCell: Components.TableCell,
                                TableRow: Components.TableRow
                            }}
                        />
                    </Components.Table>
                </Components.Col>
            </Components.Row>
            <Components.Row className="controlRow__root bottom">
                <Components.Col xs={12} md={12} className="text-right">
                    <Pagination
                        classes={classes}
                        data={data}
                        rowsPerPage={rowsPerPage}
                        currentPage={currentPage}
                        onPageNavigate={onPageNavigate}
                        labels={labels}
                        maxPage={maxPage}
                        components={{ Button: Components.Button, ButtonGroup: Components.ButtonGroup }}
                    />
                </Components.Col>
            </Components.Row>
        </div>
    );
}, shouldTableUpdate);

// Protocol column is marked filterable so the library's Filter component
// (in CustomTable) will enable searching it. Matching ProtocolMaster/MedicineMaster
// pattern: non-matching rows are hidden, pagination auto-adjusts.
const header = [
    { title: '', prop: 'pick', cellProps: { style: { width: '1%' } } },
    { title: 'Protocol', prop: 'protocol', sortable: true, filterable: true },
    { title: 'Primary Sites', prop: 'sites', sortable: true },
    { title: 'Duration', prop: 'duration', sortable: true },
    { title: 'Treatment Days', prop: 'days' },
];

const customLabels = { show: 'Display', entries: 'rows', noResults: 'No records found' };
const classes = { table: 'table-striped table-hover dataTable mt-2', thead: 'thead-light' };

// The canonical master spelling of the catch-all site, matched case- and
// whitespace-insensitively.
//
// Matched by NAME, never by id: the protocol_primary_site migration re-seeded
// primary_site_master and every previously-valid id silently changed meaning,
// so a hardcoded 60 in this file is exactly the kind of coupling that broke
// callers last time. The backend resolves this row by name for the same reason.
//
// Used only to decide whether to warn that the list is unfiltered — never sent.
const NOT_FOUND_SITE_NAME = 'not found';

// The external trials registry opened from the footer link. It takes the
// diagnosis as query parameters and does its own matching; we only hand over
// what the latest diagnosis already records.
const CLINICAL_TRIALS_URL = 'https://clinical-trials.midaslifecare.com/';

// The picked protocol reaches the row radios through context rather than
// through their props, and the rows are built once per fetch instead of once
// per render.
//
// Both halves exist for the same reason: useDatatableLifecycle resets
// currentPage to 1 from an effect keyed on the tableBody REFERENCE. Rebuilding
// the rows to mark a new radio checked therefore threw the user back to page 1
// the instant they picked a protocol off page 2 or 3 — the selection took, but
// the row they had just clicked scrolled out from under them.
//
// Context is what makes the frozen rows workable: a context update repaints the
// radios even though shouldTableUpdate memoises the table away, so the checked
// mark stays correct while the row array — and with it the current page — holds
// still. It also survives paging away from the selected row and back, which a
// defaultChecked/uncontrolled radio would not.
const SelectionContext = React.createContext({ selectedProtocolId: null, onSelect: () => { } });

function PickRadio({ protocolId }) {
    const { selectedProtocolId, onSelect } = useContext(SelectionContext);
    return (
        <Form.Check
            type="radio"
            name="protocol-pick"
            id={`protocol-pick-${protocolId}`}
            className="pl-0"
            checked={Number(selectedProtocolId) === Number(protocolId)}
            onChange={() => onSelect(protocolId)}
        />
    );
}

// Confirm New Treatment Plan.
//
// Was a plain Yes/No prompt that made no backend call. It now also lists the
// protocols indicated for the diagnosis's primary sites, so the regimen is
// chosen here and carried into the treatment plan form.
//
// The original confirmation sentence is unchanged and still sits at the top;
// the table goes underneath it.
export default class ProtocolSelectionPopup extends Component {
    constructor(props) {
        super(props);
        this.state = {
            protocolOptions: [],
            // The table rows built from protocolOptions. Kept in state — as on
            // the ten sibling screens — so the array reference only changes when
            // a fetch delivers a different list, never on a selection.
            protocolRows: [],
            loading: false,
            error: '',
            // A protocolId, or the string 'NONE', or null for "nothing picked".
            // Ok stays disabled while this is null.
            selectedProtocolId: null,
        };
    }

    componentDidUpdate(prevProps) {
        if (this.props.show && !prevProps.show) {
            this.loadProtocols();
        }
    }

    loadProtocols() {
        const siteIdsParam = toSiteIdsParam(this.props.diagnosisPrimarySites);

        // Reset the selection on every open — a choice from a previous visit
        // must never be carried into a different treatment plan.
        this.setState({ loading: true, error: '', selectedProtocolId: null, protocolOptions: [], protocolRows: [] });

        fetchProtocolsByPrimarySite(siteIdsParam)
            .then((protocols) => {
                // A fresh list is the one case where page 1 IS the right landing
                // spot, and the new rows reference makes the table reset itself.
                this.setState({
                    protocolOptions: protocols,
                    protocolRows: this.buildRows(protocols),
                    loading: false,
                });
            })
            .catch((err) => {
                // "None of these" stays available below, so a failed lookup
                // never blocks treatment-plan creation.
                this.setState({
                    loading: false,
                    protocolOptions: [],
                    protocolRows: [],
                    error: 'Could not load the protocol list. You can still continue with "None of these".',
                });
            });
    }

    // True when the response is the unfiltered ~480-row set rather than a
    // recommendation. Both cases mean the same clinical thing — no primary site
    // is actually known — and the backend widens rather than narrows on either
    // (spec §7). Worth saying out loud, or the list looks like a bug.
    isUnfiltered() {
        const sites = Array.isArray(this.props.diagnosisPrimarySites) ? this.props.diagnosisPrimarySites : [];
        if (toSiteIdsParam(sites) === '') return true;
        return sites.some(
            (site) => site && String(site.siteName || '').trim().toLowerCase() === NOT_FOUND_SITE_NAME
        );
    }

    confirmationMessage() {
        const { firstName, lastName, diagnosisDate, diagnosisPrimarySite } = this.props;
        const patient = `${firstName} ${lastName}`;
        if (diagnosisDate) {
            return `Latest diagnosis is done on ${diagnosisDate}${diagnosisPrimarySite ? ` for ${diagnosisPrimarySite}` : ''}. Do you want to proceed to create a new treatment plan for ${patient}?`;
        }
        return `Do you want to proceed to create a new treatment plan for ${patient}?`;
    }

    handleOk = () => {
        const { selectedProtocolId, protocolOptions } = this.state;
        if (selectedProtocolId === null) return;

        if (selectedProtocolId === 'NONE') {
            // No protocol chosen, so the treatment plan form's Regimen dropdown
            // should offer everything. Done by sending NO siteIds at all rather
            // than the Not Found id: both widen identically, but an empty
            // parameter keeps a re-seedable master-table id out of our source.
            this.props.onConfirm({
                protocol: null,
                siteIdsForPlan: '',
                prefetchedProtocols: null,
            });
            return;
        }

        const protocol = protocolOptions.find(
            (p) => Number(p.protocolId) === Number(selectedProtocolId)
        );
        this.props.onConfirm({
            protocol: protocol
                ? { protocolId: protocol.protocolId, protocolName: protocol.protocolName }
                : null,
            siteIdsForPlan: toSiteIdsParam(this.props.diagnosisPrimarySites),
            // The plan form's dropdown needs the same list we just fetched, so
            // pass it along rather than making it ask again.
            prefetchedProtocols: protocolOptions,
        });
    };

    // The lead site is what the registry means by "primary site". Falls back to
    // the first recorded site, and then to the prose scalar CaseProfile passes
    // for the confirmation sentence — that one is all a pre-primarySites
    // diagnosis has.
    clinicalTrialsSite() {
        const sites = Array.isArray(this.props.diagnosisPrimarySites) ? this.props.diagnosisPrimarySites : [];
        const named = sites.filter((site) => site && String(site.siteName || '').trim() !== '');
        const lead = named.find((site) => site.leadSite === true) || named[0];
        const name = lead
            ? String(lead.siteName).trim()
            : String(this.props.diagnosisPrimarySite || '').trim();
        // "Not found" is the master's catch-all, not a real site — sending it
        // would narrow the registry to nothing instead of widening it.
        return name.toLowerCase() === NOT_FOUND_SITE_NAME ? '' : name;
    }

    // Every parameter is optional and an empty one is dropped rather than sent
    // blank — the bare URL is a legitimate link, and `site=` reads to the
    // registry as a filter we cannot actually fill in.
    clinicalTrialsUrl() {
        const biomarkers = Array.isArray(this.props.diagnosisBiomarkers) ? this.props.diagnosisBiomarkers : [];
        const params = new URLSearchParams();

        const site = this.clinicalTrialsSite();
        if (site) params.set('site', site);

        const histology = String(this.props.diagnosisHistology || '').trim();
        if (histology) params.set('histology', histology);

        const biomarkerNames = biomarkers
            .map((biomarker) => String(biomarker || '').trim())
            .filter((biomarker) => biomarker !== '')
            .join(',');
        if (biomarkerNames) params.set('biomarkers', biomarkerNames);

        // Asks the registry to run the search on arrival instead of landing on a
        // filled-in form the user still has to submit. Only ever sent alongside
        // a site, because Primary Site is required over there — an auto-search
        // without one would just surface their validation error.
        //
        // Ignored until they ship support for it, which costs us nothing: an
        // unknown query parameter changes none of the current behaviour.
        if (site) params.set('autosearch', '1');

        const query = params.toString();
        return query ? `${CLINICAL_TRIALS_URL}?${query}` : CLINICAL_TRIALS_URL;
    }

    handleSelect = (protocolId) => {
        this.setState({ selectedProtocolId: protocolId });
    };

    // Called once per fetch, not per render — see SelectionContext above. The
    // rows carry no selection state of their own; PickRadio reads it from
    // context, which is why this array can be held across a selection.
    buildRows(protocolOptions) {
        return protocolOptions.map((protocol) => {
            const id = protocol.protocolId;
            return {
                pick: <PickRadio protocolId={id} />,
                protocol: protocol.protocolName || '',
                // Already joined by the backend, and never null — seven
                // protocols have no site mapped and come back as "".
                sites: protocol.primarySite || '--',
                // Null whenever no schedule is linked to the protocol.
                duration: protocol.protocolDuration || '--',
                // Null for single-day protocols.
                days: protocol.treatmentDays || '--',
            };
        });
    }

    render() {
        const { show, onHide } = this.props;
        const { loading, error, selectedProtocolId, protocolOptions, protocolRows } = this.state;

        return (
            <Modal show={show} onHide={onHide} centered size="lg" scrollable={true}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm New Treatment Plan</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Container fluid>
                        <Row>
                            <Col lg='12' className="p-0">
                                {this.confirmationMessage()}
                            </Col>
                        </Row>

                        {error && (
                            <Row className="mt-3">
                                <Col lg='12' className="p-0">
                                    <Alert variant="warning" className="mb-0">{error}</Alert>
                                </Col>
                            </Row>
                        )}

                        {!error && !loading && this.isUnfiltered() && (
                            <Row className="mt-3">
                                <Col lg='12' className="p-0">
                                    <Alert variant="info" className="mb-0">
                                        No specific primary site is recorded for this diagnosis, so every
                                        protocol is listed. Sort by a column heading to find one.
                                    </Alert>
                                </Col>
                            </Row>
                        )}

                        <Row className="mt-3">
                            <Col lg='12' className="p-0">
                                {loading ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" role="status" />
                                        <div className="mt-2">Loading protocols…</div>
                                    </div>
                                ) : (
                                    <SelectionContext.Provider
                                        value={{ selectedProtocolId, onSelect: this.handleSelect }}
                                    >
                                        <CustomTable
                                            id="datatableProtocolByPrimarySite"
                                            tableHeaders={header}
                                            tableBody={protocolRows}
                                            rowsPerPage={10}
                                            initialSort={{ prop: 'protocol', isAscending: true }}
                                            labels={customLabels}
                                            classes={classes}
                                        />
                                    </SelectionContext.Provider>
                                )}
                            </Col>
                        </Row>

                        {/* Outside the table on purpose. As a table row it would be
                            paginated away — with no primary site recorded the list
                            runs to hundreds of rows and this would land on the last
                            page. It is also the only enabled control when the list
                            is empty or the fetch failed, so it must always render. */}
                        <Row className="mt-2">
                            <Col lg='12' className="p-0">
                                <Form.Check
                                    type="radio"
                                    name="protocol-pick"
                                    id="protocol-pick-none"
                                    label="None of these"
                                    checked={selectedProtocolId === 'NONE'}
                                    onChange={() => this.setState({ selectedProtocolId: 'NONE' })}
                                />
                                {!loading && !error && protocolOptions.length === 0 && (
                                    <small className="text-muted d-block mt-1">
                                        No protocol is mapped to this primary site yet — choose
                                        "None of these" to pick a regimen on the next screen.
                                    </small>
                                )}
                            </Col>
                        </Row>
                    </Container>
                </Modal.Body>
                <Modal.Footer>
                    {/* Ok/Cancel sit centred on the DIALOG, not on whatever space
                        the link leaves over, so the two flanking cells need an
                        equal basis — `flex-fill` alone grows from content width
                        and would push the buttons off-centre by the width of the
                        link. The empty left cell is what balances the right one. */}
                    <div style={{ flex: '1 1 0' }} />
                    <div className="text-center">
                        <Button
                            variant="secondary"
                            className="mr-2"
                            onClick={this.handleOk}
                            disabled={selectedProtocolId === null}
                        >
                            Ok
                        </Button>
                        <Button variant="outline-secondary" onClick={onHide}>
                            Cancel
                        </Button>
                    </div>
                    {/* Opens in its own tab on purpose: the popup keeps whatever
                        protocol has been picked, so looking a trial up never
                        costs the user the selection. */}
                    <div className="text-right" style={{ flex: '1 1 0' }}>
                        <a
                            href={this.clinicalTrialsUrl()}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Ongoing Clinical Trials
                        </a>
                    </div>
                </Modal.Footer>
            </Modal>
        );
    }
}
